import { useEffect, useMemo, useState } from "react";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import AdminHelpTab from "./AdminHelpTab";
import { decodeHelpLanguage, readHelpEntryFormat, type HelpLanguageRecord } from "@/utils/helpContentCodec";
import { renderFormattedContent } from "@/utils/renderFormattedContent";
import "@style/sass/_helpMenu.scss";

type HelpSection = "system.help" | "company.help";

type HelpEntry = {
  id?: number;
  section?: string;
  identifier?: string;
  companyId?: number | null;
  format?: number | string | null;
  languages?: HelpLanguageRecord[];
};

type HelpMenuTabProps = {
  tabData: {
    activeTab: string | null;
    updateSpinner?: (show: boolean) => void;
    company?: { id?: number } | null;
  };
  helpSection: HelpSection;
  isSystemAdmin: boolean;
};

const EDIT_HELP_SELECTION = "edit";

function readUserFromSession(): { languageId?: number; defaultLanguageId?: number } | null {
  try {
    const userData = window.sessionStorage.getItem("User");
    if (!userData) return null;
    return JSON.parse(atob(userData));
  } catch {
    return null;
  }
}

function getHelpArticleFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("helpArticle");
}

function selectHelpLanguage(
  entry: HelpEntry,
  preferredLanguageId: number,
): HelpLanguageRecord | null {
  const languages = entry.languages ?? [];
  if (languages.length === 0) return null;

  const normalizedPreferred = String(preferredLanguageId).toLowerCase();
  const exactLanguage = languages.find(
    (lang) => String(lang.languageId ?? "").toLowerCase() === normalizedPreferred,
  );
  if (exactLanguage) return decodeHelpLanguage(exactLanguage);

  const englishLanguage = languages.find((lang) => {
    const key = String(lang.languageId ?? "").toLowerCase();
    return key === "en" || key === "eng" || key === "1";
  });
  if (englishLanguage) return decodeHelpLanguage(englishLanguage);

  return decodeHelpLanguage(languages[0]);
}

export default function HelpMenuTab(props: HelpMenuTabProps) {
  const { tabData, helpSection, isSystemAdmin } = props;
  const { activeTab, updateSpinner, company } = tabData;

  const [articles, setArticles] = useState<HelpEntry[]>([]);
  const [loadError, setLoadError] = useState("");
  const [selectedKey, setSelectedKey] = useState<string>(() => getHelpArticleFromUrl() ?? "");

  const currentUser = useMemo(() => readUserFromSession(), []);
  const preferredLanguageId = Number(currentUser?.languageId ?? currentUser?.defaultLanguageId ?? 1);

  function isOnThisTab() {
    return activeTab === "help";
  }

  const navArticles = useMemo(() => {
    return articles
      .filter((entry) => (entry.section ?? "").toLowerCase() === helpSection.toLowerCase())
      .sort((a, b) => (a.identifier ?? "").localeCompare(b.identifier ?? "", undefined, { sensitivity: "base" }));
  }, [articles, helpSection]);

  const selectedArticle = useMemo(() => {
    if (selectedKey === EDIT_HELP_SELECTION) return null;
    const articleId = Number(selectedKey);
    if (!Number.isFinite(articleId) || articleId <= 0) return null;
    return navArticles.find((entry) => entry.id === articleId) ?? null;
  }, [navArticles, selectedKey]);

  const selectedArticleLanguage = useMemo(() => {
    if (!selectedArticle) return null;
    return selectHelpLanguage(selectedArticle, preferredLanguageId);
  }, [selectedArticle, preferredLanguageId]);

  useEffect(() => {
    if (!isOnThisTab()) return;
    void loadArticlesAsync();
  }, [activeTab, helpSection, company?.id]);

  const selectedArticleFormat = useMemo(() => {
    if (!selectedArticle) return Enums.CONTENT_FORMAT.Text;
    return readHelpEntryFormat(selectedArticle);
  }, [selectedArticle]);

  useEffect(() => {
    if (!isOnThisTab()) return;

    if (selectedKey === EDIT_HELP_SELECTION) {
      if (isSystemAdmin) return;
      if (navArticles.length > 0) {
        setSelectedKey(String(navArticles[0].id));
      } else {
        setSelectedKey("");
      }
      return;
    }

    if (navArticles.length === 0) {
      if (isSystemAdmin && selectedKey !== EDIT_HELP_SELECTION) {
        setSelectedKey(EDIT_HELP_SELECTION);
      }
      return;
    }

    if (selectedKey === EDIT_HELP_SELECTION && isSystemAdmin) return;

    const articleId = Number(selectedKey);
    const hasValidSelection = navArticles.some((entry) => entry.id === articleId);
    if (hasValidSelection) return;

    const firstArticleId = navArticles[0]?.id;
    if (firstArticleId) {
      setSelectedKey(String(firstArticleId));
    } else if (isSystemAdmin) {
      setSelectedKey(EDIT_HELP_SELECTION);
    }
  }, [navArticles, isSystemAdmin, selectedKey, activeTab]);

  useEffect(() => {
    if (!isOnThisTab() || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    if (selectedKey) {
      params.set("helpArticle", selectedKey);
    } else {
      params.delete("helpArticle");
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  }, [selectedKey, activeTab]);

  async function loadArticlesAsync() {
    setLoadError("");
    if (typeof updateSpinner === "function") updateSpinner(true);

    try {
      const url = helpSection === "system.help"
        ? `/Help/Section/${helpSection}`
        : `/Help/Company/${company?.id ?? 0}/Section/${helpSection}`;

      const resp = await RightTimeApi.getAsync(url);
      if (resp?.status === 403) {
        setArticles([]);
        setLoadError("You do not have permission to view help articles.");
        return;
      }

      if (resp?.ok && Array.isArray(resp.jsonData)) {
        setArticles(resp.jsonData);
      } else {
        setArticles([]);
        setLoadError("Unable to load help articles.");
      }
    } catch {
      setArticles([]);
      setLoadError("Unable to load help articles.");
    } finally {
      if (typeof updateSpinner === "function") updateSpinner(false);
    }
  }

  function handleSelectEditHelp() {
    setSelectedKey(EDIT_HELP_SELECTION);
  }

  function handleSelectArticle(articleId: number) {
    setSelectedKey(String(articleId));
  }

  if (!isOnThisTab()) return null;

  const showEditPane = isSystemAdmin && selectedKey === EDIT_HELP_SELECTION;
  const showEmptyArticles = !showEditPane && !selectedArticle && navArticles.length === 0;

  return (
    <div className="helpMenuTab">
      <nav className="helpMenuNav" aria-label="Help articles">
        {isSystemAdmin && (
          <button
            type="button"
            className={`helpMenuNavEditItem${selectedKey === EDIT_HELP_SELECTION ? " active" : ""}`}
            onClick={handleSelectEditHelp}
          >
            Edit Help
          </button>
        )}
        {navArticles.map((entry) => (
          <button
            key={entry.id ?? entry.identifier}
            type="button"
            className={selectedKey === String(entry.id) ? "active" : ""}
            onClick={() => handleSelectArticle(entry.id ?? 0)}
          >
            {entry.identifier || "Untitled"}
          </button>
        ))}
      </nav>

      <div className="helpMenuContent">
        {loadError && <p className="helpMenuError">{loadError}</p>}

        {showEditPane && (
          <AdminHelpTab
            tabData={tabData}
            embedded
            defaultSectionFilter={helpSection}
            onContentChanged={() => { void loadArticlesAsync(); }}
          />
        )}

        {!showEditPane && selectedArticle && (
          <>
            <h1 className="helpMenuArticleTitle">{selectedArticle.identifier}</h1>
            <div className="helpMenuArticleBody">
              {selectedArticleLanguage?.content
                ? renderFormattedContent(selectedArticleLanguage.content, { format: selectedArticleFormat })
                : <p className="helpMenuEmpty">No content is available for this article in your language.</p>}
            </div>
          </>
        )}

        {showEmptyArticles && !loadError && (
          <p className="helpMenuEmpty">No help articles are available yet.</p>
        )}
      </div>
    </div>
  );
}
